/**
 * Version tag for the voice/video call PUSH payload contract (VoIP pushes on
 * iOS, FCM data messages on Android/web). Shared by the backend (which stamps
 * every call push with `signalVersion`) and the clients (which parse it
 * TOLERANTLY — an absent or unknown version must never break call handling,
 * only be logged so contract drift between deployed lambdas and store builds
 * is detectable in the field).
 *
 * Bump when a call push payload key changes meaning, is removed, or a new key
 * becomes required. Additive optional keys do NOT need a bump.
 *
 * See docs/call-release-protocol.md for the deploy-order rules this supports.
 */

export const CALL_PUSH_SIGNAL_VERSION = 1

/**
 * Tolerant client-side parse of the `signalVersion` push field. Returns the
 * numeric version, or null when absent/unparseable (old backend or old push
 * shape — treat as "version 1 era" and continue).
 */
export function parseCallPushSignalVersion(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

/** True when the push comes from a NEWER contract than this client knows. */
export function isNewerCallPushContract(raw: unknown): boolean {
  const version = parseCallPushSignalVersion(raw)
  return version !== null && version > CALL_PUSH_SIGNAL_VERSION
}
